import React from 'react';
import { X } from 'lucide-react';
import { ScanTextInput } from '../../../../common/inputs';
import { MobileBarcodeCaptureInput } from '../../../../common/scanning';
import { useBarcodeScannerEnabled } from '../../../../../hooks/useBarcodeScannerEnabled';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface LocationScanModalProps {
  isOpen: boolean;
  expectedLocation: string;
  tempGS1Quantity?: number | null;
  locationScan: string;
  onLocationScanChange: (value: string) => void;
  onConfirm: (scannedValue?: string) => void;
  onOverride: () => void;
  onClose: () => void;
}

export const LocationScanModal: React.FC<LocationScanModalProps> = ({
  isOpen,
  expectedLocation,
  tempGS1Quantity,
  locationScan,
  onLocationScanChange,
  onConfirm,
  onOverride,
  onClose
}) => {
  const { t } = useTranslation();
  // Punkt 4: global skann-capture må være aktiv også når denne modalen er åpen, siden
  // foreldrenes globale skannere deaktiveres (isAnyModalOpen) mens modalen vises.
  const globalScanEnabled = useBarcodeScannerEnabled();
  if (!isOpen) return null;

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onConfirm();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full h-full sm:h-auto sm:rounded-lg p-4 sm:p-6 sm:max-w-md overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{t('warehouse.modules.picking.locationScan.title')}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={24} />
          </button>
        </div>
        
        <p className="text-sm text-gray-600 mb-4">
          {t('warehouse.modules.picking.locationScan.expectedLocation')}: <span className="font-medium">{expectedLocation}</span>
          {tempGS1Quantity && (
            <><br/>{t('common.fields.quantity')}: <span className="font-medium text-blue-600">{tempGS1Quantity}</span></>
          )}
        </p>
        
        {/* Punkt 4: skjult fokus-eiende capture for enterprise/nettbrett-skannere.
            Foreldrenes globale skannere er deaktivert mens denne modalen er åpen, så
            lokasjon-skann må fanges her. Mirrorer pakk (commit ea24ef17). Skannet verdi
            sendes direkte til onConfirm for å unngå setState-timing. */}
        <MobileBarcodeCaptureInput
          enabled={globalScanEnabled}
          onScan={(barcode) => {
            const cleaned = barcode.replace(/[~§]+$/g, '').trim();
            onLocationScanChange(cleaned);
            onConfirm(cleaned);
          }}
        />

        <ScanTextInput
          placeholder={t('warehouse.modules.picking.locationScan.scanPlaceholder')}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-4 min-h-[44px]"
          value={locationScan}
          onChange={(e) => onLocationScanChange(e.target.value)}
          onKeyPress={handleKeyPress}
          autoFocus={!globalScanEnabled}
        />

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => onConfirm()}
            className="flex-1 bg-blue-600 text-white py-3 sm:py-2 rounded-lg hover:bg-blue-700 min-h-[44px] order-1"
          >
            {t('common.buttons.confirm')}
          </button>
          <button
            onClick={onOverride}
            className="flex-1 bg-gray-600 text-white py-3 sm:py-2 rounded-lg hover:bg-gray-700 min-h-[44px] order-2"
          >
            {t('warehouse.modules.picking.locationScan.override')}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-3 sm:py-2 border border-gray-300 rounded-lg hover:bg-gray-50 min-h-[44px] order-3"
          >
            {t('common.buttons.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};